import React, { useEffect, useMemo, useState } from 'react'
import {
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from 'react-native'
import { useTranslation } from 'react-i18next'
import {
  Button,
  COMMON_STYLES,
  FloatingLabelInput,
  LazyImage,
  SIZE,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import { DemoCreateRequestScreenProps, ROUTES } from 'app/routes/types'
import fetchAllCrops from 'app/farmer360/apis/fetchAllCrops'
import { Crop } from 'app/farmer360/models/crop'
import BigInput from 'app/components/reusable/BigInput'
import fetchLocationDetails from 'app/farmer360/apis/fetchLocationDetails'
import getVillagesByBlock from 'app/farmer360/apis/getVillagesByBlock'
import ToastAndroid from 'app/mocks/ToastAndroid'
import fetchStateNodes from 'app/apis/fetchStateNodes'
import { Node, VendorRequest } from 'app/models/RequestFlow'
import styles from './demoCreateRequest.styles'
import DemoCreateRequestSheets from 'app/components/demoMaterial/DemoCreateRequestSheets'
import useDebounce from 'app/hooks/useDebounce'
import useFetchVendors from 'app/hooks/useFetchVendors'
import { useSelector } from 'react-redux'
import { selectRoleType } from 'app/store/reducer/requestflow'

type VendorSelection = {
  vendor_id: number
  vendor_name: string
  dc_state_id: number
  partner_id: string
  node_name: string
  partner_location: string
  partner_address: string
}

const vendorSelectionFromDraft = (draft: DemoCreateRequestScreenProps['route']['params']['draft']): VendorSelection => ({
  vendor_id: Number(draft.vendor_id || 0),
  vendor_name: draft.vendor_name || '',
  dc_state_id: Number(draft.dc_state_id || 0),
  partner_id: draft.partner_id || '',
  node_name: draft.node_name || draft.inventory_node_name || '',
  partner_location: draft.partner_location || '',
  partner_address: draft.partner_address || '',
})

const mapVendorToSelection = (item: VendorRequest): VendorSelection => ({
  vendor_id: item.id,
  vendor_name: item.company_name,
  dc_state_id: item.state?.id || 0,
  partner_id: item.partner_id || '',
  node_name: item.node?.name || '',
  partner_location: [item.block?.name, item.district?.name, item.state?.name]
    .filter(Boolean)
    .join(', '),
  partner_address: item.company_address || '',
})

const DemoCreateRequest = ({ navigation, route }: DemoCreateRequestScreenProps) => {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const roleType = useSelector(selectRoleType)
  const draft = route.params.draft
  const [vendorSelection, setVendorSelection] = useState<VendorSelection>(() =>
    vendorSelectionFromDraft(draft),
  )
  const [form, setForm] = useState({
    farmer_name: draft.farmer_name || '',
    farmer_mobile: draft.farmer_mobile || '',
    address: draft.address || '',
    pin_code: draft.pin_code || '',
    state_id: draft.state_id || 0,
    district_id: draft.district_id || 0,
    block_id: draft.block_id || 0,
    village_id: draft.village_id || 0,
    state_name: draft.state_name || '',
    district_name: draft.district_name || '',
    block_name: draft.block_name || '',
    village_name: draft.village_name || '',
    crop_id: draft.crop_id || 0,
    crop_name: draft.crop_name || '',
    crop_image_url: '',
    inventory_node_id: draft.inventory_node_id || 0,
    inventory_node_name: draft.inventory_node_name || draft.node_name || '',
    remarks: draft.remarks || '',
  })
  const [showCropSheet, setShowCropSheet] = useState(false)
  const [cropSearch, setCropSearch] = useState('')
  const [crops, setCrops] = useState<Crop[]>([])
  const [cropLoading, setCropLoading] = useState(false)
  const [cropSheetOpening, setCropSheetOpening] = useState(false)
  const [showVillageSheet, setShowVillageSheet] = useState(false)
  const [showDistrictSheet, setShowDistrictSheet] = useState(false)
  const [showBlockSheet, setShowBlockSheet] = useState(false)
  const [showVendorSheet, setShowVendorSheet] = useState(false)
  const [vendorFetchEnabled, setVendorFetchEnabled] = useState(false)
  const [vendorSearch, setVendorSearch] = useState('')
  const debouncedVendorSearch = useDebounce(vendorSearch)
  const [villageSearch, setVillageSearch] = useState('')
  const [districtSearch, setDistrictSearch] = useState('')
  const [blockSearch, setBlockSearch] = useState('')
  const [villages, setVillages] = useState<{ id: string; name: string }[]>([])
  const [districts, setDistricts] = useState<{ id: string; name: string }[]>([])
  const [blocks, setBlocks] = useState<{ id: string; name: string }[]>([])
  const [locationLoading, setLocationLoading] = useState(false)
  const [pincodeLookupFailed, setPincodeLookupFailed] = useState(false)
  const [inventoryNodeLoading, setInventoryNodeLoading] = useState(false)
  const [inventoryNodes, setInventoryNodes] = useState<Node[]>([])
  const [inventoryNodeSearch, setInventoryNodeSearch] = useState('')
  const [showInventoryNodeSheet, setShowInventoryNodeSheet] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const {
    request: vendors,
    loading: vendorLoading,
    loadingNext: vendorLoadingNext,
    fetchNext: fetchNextVendors,
  } = useFetchVendors({
    search_query: debouncedVendorSearch,
    role_type: roleType,
    enabled: vendorFetchEnabled,
  })

  const openVendorSheet = () => {
    setVendorFetchEnabled(true)
    setVendorSearch('')
    setShowVendorSheet(true)
  }

  useEffect(() => {
    if (!draft.product_variant_id) {
      ToastAndroid.show(t('demo_create_select_variant_first'), ToastAndroid.SHORT)
      navigation.replace(ROUTES.DEMO_PRODUCT_SELECTION)
    }
  }, [draft.product_variant_id, navigation, t])

  const validation = useMemo(() => {
    return {
      vendor_id: !vendorSelection.vendor_id,
      farmer_name: !form.farmer_name.trim(),
      farmer_mobile: form.farmer_mobile.length !== 10,
      crop_id: !form.crop_id,
      inventory_node_id: !form.inventory_node_id,
      pin_code: form.pin_code.length !== 6,
      state_id: !form.state_id,
      district_id: !form.district_id,
      block_id: !form.block_id,
      village_id: !form.village_id,
      address: !form.address.trim(),
    }
  }, [form, vendorSelection.vendor_id])

  const requiredValid = useMemo(
    () => !Object.values(validation).some(Boolean),
    [validation],
  )

  const setValue = (key: keyof typeof form, value: string | number) =>
    setForm(prev => ({ ...prev, [key]: value }))

  const loadCrops = async () => {
    if (crops.length > 0) return
    setCropLoading(true)
    try {
      const response = await fetchAllCrops()
      const flattened = (response || []).flatMap(item => item.crops || [])
      setCrops(flattened)
    } finally {
      setCropLoading(false)
    }
  }

  const openCropSheet = async () => {
    setCropSheetOpening(true)
    try {
      await loadCrops()
      setShowCropSheet(true)
    } finally {
      setCropSheetOpening(false)
    }
  }

  const loadInventoryNodes = async (stateId: number) => {
    if (!stateId) {
      setInventoryNodes([])
      return
    }
    setInventoryNodeLoading(true)
    const response = await fetchStateNodes(stateId)
    const nodes = response || []
    setInventoryNodes(nodes)
    setForm(prev => {
      if (!prev.inventory_node_id) return prev
      const exists = nodes.some(node => node.id === Number(prev.inventory_node_id))
      if (exists) return prev
      return { ...prev, inventory_node_id: 0, inventory_node_name: '' }
    })
    setInventoryNodeLoading(false)
  }

  useEffect(() => {
    if (vendorSelection.dc_state_id) {
      loadInventoryNodes(vendorSelection.dc_state_id)
    }
  }, [vendorSelection.dc_state_id])

  const handleSelectVendor = async (item: VendorRequest) => {
    const selection = mapVendorToSelection(item)
    if (!selection.dc_state_id) {
      ToastAndroid.show(t('demo_create_unable_find_dc_state'), ToastAndroid.SHORT)
      return
    }
    setVendorSelection(selection)
    setShowVendorSheet(false)
    const nodeId = item.node?.id || 0
    const nodeName = item.node?.name || ''
    setForm(prev => ({
      ...prev,
      inventory_node_id: nodeId,
      inventory_node_name: nodeName,
    }))
    await loadInventoryNodes(selection.dc_state_id)
  }

  const clearLocationDependentFields = () => {
    setForm(prev => ({
      ...prev,
      state_name: '',
      district_name: '',
      block_name: '',
      village_name: '',
      state_id: 0,
      district_id: 0,
      block_id: 0,
      village_id: 0,
    }))
    setDistricts([])
    setBlocks([])
    setVillages([])
  }

  const handlePinCodeChange = async (value: string) => {
    const pin = value.replace(/[^0-9]/g, '').slice(0, 6)
    setValue('pin_code', pin)
    if (pin.length !== 6) {
      if (pincodeLookupFailed) setPincodeLookupFailed(false)
      return
    }

    setLocationLoading(true)
    setPincodeLookupFailed(false)
    const location = await fetchLocationDetails(pin)
    if (!location || Number(location.kheti_state_id || 0) === 0) {
      setLocationLoading(false)
      setPincodeLookupFailed(true)
      clearLocationDependentFields()
      return
    }

    setForm(prev => ({
      ...prev,
      pin_code: pin,
      state_name: location.state || '',
      district_name: location.district || '',
      block_name: location.block || '',
      state_id: Number(location.kheti_state_id || 0),
      district_id: Number(location.kheti_district_id || 0),
      block_id: Number(location.kheti_block_id || 0),
      village_id: 0,
      village_name: '',
    }))

    const villageList = await getVillagesByBlock(String(location.kheti_block_id || ''))
    setVillages(villageList || [])
    setDistricts(
      location.kheti_district_id
        ? [{ id: String(location.kheti_district_id), name: location.district || '' }]
        : [],
    )
    setBlocks(
      location.kheti_block_id
        ? [{ id: String(location.kheti_block_id), name: location.block || '' }]
        : [],
    )
    setLocationLoading(false)
  }

  const filteredCrops = crops.filter(c =>
    c.name.toLowerCase().includes(cropSearch.toLowerCase()),
  )
  const filteredVillages = villages.filter(v =>
    v.name.toLowerCase().includes(villageSearch.toLowerCase()),
  )
  const filteredDistricts = districts.filter(v =>
    v.name.toLowerCase().includes(districtSearch.toLowerCase()),
  )
  const filteredBlocks = blocks.filter(v =>
    v.name.toLowerCase().includes(blockSearch.toLowerCase()),
  )
  const filteredInventoryNodes = inventoryNodes.filter(node =>
    node.name.toLowerCase().includes(inventoryNodeSearch.toLowerCase()),
  )
  const cropColumns = useMemo(() => {
    const usableWidth = Math.max(280, width - 48)
    return Math.max(2, Math.floor(usableWidth / 96))
  }, [width])

  return (
    <View style={[COMMON_STYLES.flex1, styles.screenBg]}>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>{t('demo_create_section_farmer_details')}</Text>
        <FloatingLabelInput
          title={t('demo_create_farmer_name_label')}
          titleFocusedStyle={TEXT_STYLES.textSuccess100}
          value={form.farmer_name}
          maxLength={50}
          onChangeText={v =>
            setValue(
              'farmer_name',
              v.replace(/[^a-zA-Z\s.'-]/g, '').replace(/\s{2,}/g, ' '),
            )
          }
          error={showErrors && validation.farmer_name}
          errorText={t('demo_create_farmer_name_required')}
          containerStyle={styles.fieldWrap}
        />
        <BigInput
          title={t('demo_create_farmer_phone_label')}
          placeholder={t('demo_create_phone_placeholder')}
          value={form.farmer_mobile}
          onChangeText={(v: string) =>
            setValue('farmer_mobile', v.replace(/[^0-9]/g, '').slice(0, 10))
          }
          maxLength={10}
          leftIcon={<Text style={styles.phonePrefixText}>+91</Text>}
          style={styles.phoneBigInput}
          leftIconContainerStyle={styles.phoneBigInputLeft}
          inputStyle={styles.phoneBigInputText}
          labelStyle={styles.phoneBigInputLabel}
        />
        {showErrors && validation.farmer_mobile ? (
          <Text style={styles.errorText}>{t('demo_create_phone_invalid')}</Text>
        ) : null}
        <Text style={styles.phoneCaption}>{t('demo_create_phone_caption')}</Text>

        <Text style={styles.section}>{t('demo_create_section_add_crop')}</Text>
        <View style={[styles.cropWrap, COMMON_STYLES.row, COMMON_STYLES.alignItemsStart]}>
          <View style={styles.addCropTile}>
            <Pressable
              style={styles.addCropCircle}
              onPress={openCropSheet}
              disabled={cropSheetOpening}
            >
              <Text style={styles.addCropPlus}>+</Text>
            </Pressable>
            <Text style={styles.addCropText}>{t('demo_create_add_crop')}</Text>
          </View>
          {form.crop_name ? (
            <View style={styles.selectedCropCard}>
              <LazyImage
                imageUrl={form.crop_image_url}
                imgStyles={styles.selectedCropImage}
                fallBackIcon={<View style={styles.selectedCropImageFallback} />}
              />
              <Text numberOfLines={2} style={styles.selectedCropName}>
                {form.crop_name}
              </Text>
            </View>
          ) : null}
        </View>
        {showErrors && validation.crop_id ? (
          <Text style={styles.errorText}>{t('demo_create_crop_required')}</Text>
        ) : null}

        <Text style={styles.section}>{t('demo_create_section_address')}</Text>
        <FloatingLabelInput
          title={t('demo_create_pincode_label')}
          titleFocusedStyle={TEXT_STYLES.textSuccess100}
          value={form.pin_code}
          onChangeText={handlePinCodeChange}
          keyboardType="number-pad"
          maxLength={6}
          error={pincodeLookupFailed || (showErrors && validation.pin_code)}
          errorText={
            pincodeLookupFailed
              ? t('demo_create_pincode_invalid')
              : t('demo_create_pincode_length_invalid')
          }
          containerStyle={styles.fieldWrap}
        />
        <FloatingLabelInput
          title={t('demo_create_state_label')}
          titleFocusedStyle={TEXT_STYLES.textSuccess100}
          value={form.state_name}
          isDisable
          isInputEditable={false}
          onChangeText={() => undefined}
          error={showErrors && validation.state_id}
          errorText={t('demo_create_state_required')}
          containerStyle={styles.fieldWrap}
        />
        <Pressable style={styles.selectField} onPress={() => setShowDistrictSheet(true)}>
          <View style={COMMON_STYLES.flex1}>
            <Text style={styles.selectFieldLabel}>{t('demo_create_district_label')}</Text>
            <Text style={styles.selectFieldText}>
              {form.district_name || t('demo_create_select_district')}
            </Text>
          </View>
          <View style={styles.downIconWrap}>
            <View style={styles.dropdownTriangle} />
          </View>
        </Pressable>
        {showErrors && validation.district_id ? (
          <Text style={styles.errorText}>{t('demo_create_district_required')}</Text>
        ) : null}
        <Pressable style={styles.selectField} onPress={() => setShowBlockSheet(true)}>
          <View style={COMMON_STYLES.flex1}>
            <Text style={styles.selectFieldLabel}>{t('demo_create_block_label')}</Text>
            <Text style={styles.selectFieldText}>
              {form.block_name || t('demo_create_select_block')}
            </Text>
          </View>
          <View style={styles.downIconWrap}>
            <View style={styles.dropdownTriangle} />
          </View>
        </Pressable>
        {showErrors && validation.block_id ? (
          <Text style={styles.errorText}>{t('demo_create_block_required')}</Text>
        ) : null}
        <Pressable style={styles.selectField} onPress={() => setShowVillageSheet(true)}>
          <View style={COMMON_STYLES.flex1}>
            <Text style={styles.selectFieldLabel}>{t('demo_create_village_label')}</Text>
            <Text style={styles.selectFieldText}>
              {form.village_name || t('demo_create_select_village')}
            </Text>
          </View>
          <View style={styles.downIconWrap}>
            <View style={styles.dropdownTriangle} />
          </View>
        </Pressable>
        {showErrors && validation.village_id ? (
          <Text style={styles.errorText}>{t('demo_create_village_required')}</Text>
        ) : null}
        <FloatingLabelInput
          title={t('demo_create_address_label')}
          titleFocusedStyle={TEXT_STYLES.textSuccess100}
          value={form.address}
          onChangeText={v => setValue('address', v)}
          error={showErrors && validation.address}
          errorText={t('demo_create_address_required')}
          containerStyle={styles.fieldWrap}
        />
        </View>

        <View style={styles.sectionDividerBand} />

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>{t('demo_create_section_dc_mdc')}</Text>
          <Pressable style={styles.selectField} onPress={openVendorSheet}>
            <View style={COMMON_STYLES.flex1}>
              <Text style={styles.selectFieldLabel}>{t('demo_create_dc_label')}</Text>
              <Text style={styles.selectFieldText}>
                {vendorSelection.vendor_name || t('demo_create_select_dc')}
              </Text>
            </View>
            <View style={styles.downIconWrap}>
              <View style={styles.dropdownTriangle} />
            </View>
          </Pressable>
          {showErrors && validation.vendor_id ? (
            <Text style={styles.errorText}>{t('demo_create_dc_required')}</Text>
          ) : null}
          <Text style={styles.fieldNote}>{t('demo_create_dc_note')}</Text>
        </View>

        <View style={styles.sectionDividerBand} />

        <View style={styles.formSection}>
          <Text style={styles.sectionTitle}>{t('demo_create_section_node')}</Text>
          <Pressable
            style={styles.selectField}
            onPress={async () => {
              if (!vendorSelection.vendor_id) {
                ToastAndroid.show(t('demo_select_dc_first'), ToastAndroid.SHORT)
                return
              }
              if (!vendorSelection.dc_state_id) {
                ToastAndroid.show(t('demo_create_unable_find_dc_state'), ToastAndroid.SHORT)
                return
              }
              if (inventoryNodes.length === 0) {
                await loadInventoryNodes(vendorSelection.dc_state_id)
              }
              setInventoryNodeSearch('')
              setShowInventoryNodeSheet(true)
            }}
          >
            <View style={COMMON_STYLES.flex1}>
              <Text style={styles.selectFieldLabel}>{t('demo_create_inventory_node_label')}</Text>
              <Text style={styles.selectFieldText}>
                {form.inventory_node_name || t('demo_create_select_inventory_node')}
              </Text>
            </View>
            <View style={styles.downIconWrap}>
              <View style={styles.dropdownTriangle} />
            </View>
          </Pressable>
          {showErrors && validation.inventory_node_id ? (
            <Text style={styles.errorText}>{t('demo_create_inventory_node_required')}</Text>
          ) : null}
          <Text style={styles.fieldNote}>{t('demo_create_node_note')}</Text>
        </View>
      </ScrollView>
      <View style={styles.footer}>
        <Button
          text={t('demo_create_save_next')}
          size={SIZE.large}
          onClick={() => {
            if (locationLoading) return
            if (!draft.product_variant_id) {
              ToastAndroid.show(t('demo_create_select_variant_first'), ToastAndroid.SHORT)
              return
            }
            if (!requiredValid) {
              setShowErrors(true)
              return
            }
            navigation.navigate(ROUTES.DEMO_REVIEW_REQUEST, {
              draft: {
                ...route.params.draft,
                ...vendorSelection,
                node_name: vendorSelection.node_name,
                farmer_name: form.farmer_name.trim(),
                farmer_mobile: form.farmer_mobile,
                address: form.address.trim(),
                pin_code: form.pin_code,
                state_id: Number(form.state_id),
                district_id: Number(form.district_id),
                block_id: Number(form.block_id),
                village_id: Number(form.village_id),
                state_name: form.state_name,
                district_name: form.district_name,
                block_name: form.block_name,
                village_name: form.village_name,
                crop_id: form.crop_id ? Number(form.crop_id) : undefined,
                crop_name: form.crop_name || undefined,
                inventory_node_name: form.inventory_node_name || undefined,
                inventory_node_id: form.inventory_node_id
                  ? Number(form.inventory_node_id)
                  : undefined,
                remarks: form.remarks || undefined,
              },
            })
          }}
        />
      </View>
      <DemoCreateRequestSheets
        styles={styles}
        showVendorSheet={showVendorSheet}
        vendorSheetHeading={t('demo_create_select_dc_sheet_heading')}
        vendorSearch={vendorSearch}
        setVendorSearch={setVendorSearch}
        vendorLoading={vendorLoading}
        vendorLoadingNext={vendorLoadingNext}
        vendors={vendors}
        onCloseVendorSheet={() => setShowVendorSheet(false)}
        onSelectVendor={handleSelectVendor}
        onVendorEndReached={() => {
          if (!vendorLoadingNext) fetchNextVendors()
        }}
        showCropSheet={showCropSheet}
        cropSearch={cropSearch}
        setCropSearch={setCropSearch}
        cropLoading={cropLoading}
        filteredCrops={filteredCrops}
        cropColumns={cropColumns}
        selectedCropId={form.crop_id}
        onCloseCropSheet={() => setShowCropSheet(false)}
        onSelectCrop={item => {
          setForm(prev => ({
            ...prev,
            crop_id: Number(item.id),
            crop_name: item.name,
            crop_image_url: item.image_url || '',
          }))
          setShowCropSheet(false)
        }}
        showVillageSheet={showVillageSheet}
        villageSearch={villageSearch}
        setVillageSearch={setVillageSearch}
        filteredVillages={filteredVillages}
        onCloseVillageSheet={() => setShowVillageSheet(false)}
        onSelectVillage={item => {
          setForm(prev => ({
            ...prev,
            village_id: Number(item.id),
            village_name: item.name,
          }))
          setShowVillageSheet(false)
        }}
        showDistrictSheet={showDistrictSheet}
        districtSearch={districtSearch}
        setDistrictSearch={setDistrictSearch}
        filteredDistricts={filteredDistricts}
        onCloseDistrictSheet={() => setShowDistrictSheet(false)}
        onSelectDistrict={item => {
          setForm(prev => ({
            ...prev,
            district_id: Number(item.id),
            district_name: item.name,
          }))
          setShowDistrictSheet(false)
        }}
        showBlockSheet={showBlockSheet}
        blockSearch={blockSearch}
        setBlockSearch={setBlockSearch}
        filteredBlocks={filteredBlocks}
        onCloseBlockSheet={() => setShowBlockSheet(false)}
        onSelectBlock={item => {
          setForm(prev => ({
            ...prev,
            block_id: Number(item.id),
            block_name: item.name,
          }))
          setShowBlockSheet(false)
        }}
        showInventoryNodeSheet={showInventoryNodeSheet}
        inventoryNodeSearch={inventoryNodeSearch}
        setInventoryNodeSearch={setInventoryNodeSearch}
        inventoryNodeLoading={inventoryNodeLoading}
        filteredInventoryNodes={filteredInventoryNodes}
        onCloseInventoryNodeSheet={() => setShowInventoryNodeSheet(false)}
        onSelectInventoryNode={item => {
          setForm(prev => ({
            ...prev,
            inventory_node_id: Number(item.id),
            inventory_node_name: item.name,
          }))
          setShowInventoryNodeSheet(false)
        }}
      />
      {(locationLoading || cropSheetOpening) && <Spinner type="on-screen" />}
    </View>
  )
}

export default DemoCreateRequest
